(() => {
  if (customElements.get('ba-product-carousel')) return;

  class BaProductCarousel extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._cleanups = [];
      this._slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this._dots = Array.from(this.querySelectorAll('[data-carousel-to]'));
      this._current = this.querySelector('[data-carousel-current]');
      this._status = this.querySelector('[data-carousel-status]');
      this._document = this.ownerDocument;
      this._window = this._document.defaultView;
      this._index = -1;
      this._timer = null;
      this._transitionTimer = null;
      this._pointer = null;
      this._suppressClickUntil = 0;
      this._intersecting = !this._window.IntersectionObserver;
      const interval = Number(this.dataset.interval);
      this._interval = Number.isFinite(interval) && interval >= 5000 ? interval : 10000;

      const controls = this.querySelector('[data-carousel-controls]');
      if (controls) controls.hidden = this._slides.length < 2;
      this.querySelectorAll('[data-carousel-prev], [data-carousel-next], [data-carousel-to]')
        .forEach((button) => { button.disabled = this._slides.length < 2; });
      if (this._status) this._status.setAttribute('aria-live', 'polite');
      this._show(0, false);

      this._listen(this, 'click', (event) => this._onClick(event));
      this._listen(this, 'pointerdown', (event) => this._onPointerDown(event), { passive: true });
      this._listen(this, 'pointerup', (event) => this._onPointerUp(event));
      this._listen(this, 'pointercancel', () => { this._pointer = null; }, { passive: true });
      this._listen(this._document, 'visibilitychange', () => {
        if (this._document.visibilityState === 'hidden') this._settleTransition();
        this._schedule();
      });
      this._listen(this._document, 'shopify:block:select', (event) => {
        const id = String(event.detail?.blockId || '');
        const index = this._slides.findIndex((slide) => slide.dataset.carouselBlock === id);
        if (id && index >= 0) this._show(index, true);
      });

      if (this._window.IntersectionObserver) {
        this._observer = new this._window.IntersectionObserver((entries) => {
          const entry = entries.find((item) => item.target === this);
          if (!entry) return;
          this._intersecting = entry.isIntersecting;
          if (!this._intersecting) this._settleTransition();
          this._schedule();
        }, { threshold: 0.1 });
        this._observer.observe(this);
      }
      this._schedule();
    }

    disconnectedCallback() {
      this._connected = false;
      this._clearTimer();
      this._settleTransition();
      this._observer?.disconnect();
      this._observer = null;
      this._cleanups?.forEach((cleanup) => cleanup());
      this._cleanups = [];
      this._pointer = null;
    }

    _listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this._cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    _clearTimer() {
      if (this._timer === null) return;
      this._window.clearTimeout(this._timer);
      this._timer = null;
    }

    _schedule() {
      this._clearTimer();
      if (!this._connected || this._slides.length < 2 || !this._intersecting || this._document.visibilityState === 'hidden') return;
      this._timer = this._window.setTimeout(() => {
        this._timer = null;
        if (this._connected) this._show(this._index + 1, false);
      }, this._interval);
    }

    _settleTransition() {
      if (this._transitionTimer !== null) {
        this._window.clearTimeout(this._transitionTimer);
        this._transitionTimer = null;
      }
      this._slides.forEach((slide, index) => {
        const active = index === this._index;
        slide.classList.remove('is-entering', 'is-leaving');
        slide.hidden = !active;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('data-active', active);
      });
    }

    _show(index, manual) {
      if (!this._slides.length) {
        if (this._current) this._current.textContent = '0';
        return;
      }
      const nextIndex = ((index % this._slides.length) + this._slides.length) % this._slides.length;
      const outgoing = this._slides[this._index];
      const changed = nextIndex !== this._index;
      const animate = changed && !!outgoing;
      this._settleTransition();
      if (changed && outgoing?.contains(this._document.activeElement)) this.focus({ preventScroll: true });
      this._index = nextIndex;
      this._slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this._index;
        const leaving = animate && slide === outgoing;
        slide.hidden = !active && !leaving;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('data-active', active);
        if (active && animate) slide.classList.add('is-entering');
        if (leaving) slide.classList.add('is-leaving');
      });
      if (animate) {
        this._transitionTimer = this._window.setTimeout(() => this._settleTransition(), 650);
      }
      this._dots.forEach((dot) => {
        const active = Number(dot.dataset.carouselTo) === this._index;
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        dot.toggleAttribute('data-active', active);
      });
      if (this._current) this._current.textContent = String(this._index + 1);
      if (manual && this._status) this._status.textContent = this._slides[this._index].getAttribute('aria-label') || '';
      this._schedule();
    }

    _onClick(event) {
      if (Date.now() < this._suppressClickUntil) {
        this._suppressClickUntil = 0;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const button = event.target.closest('[data-carousel-prev], [data-carousel-next], [data-carousel-to]');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.hasAttribute('data-carousel-prev')) {
        this._show(this._index - 1, true);
      } else if (button.hasAttribute('data-carousel-next')) {
        this._show(this._index + 1, true);
      } else {
        const index = Number(button.dataset.carouselTo);
        if (Number.isInteger(index) && index >= 0 && index < this._slides.length) this._show(index, true);
      }
    }

    _onPointerDown(event) {
      if (event.pointerType !== 'touch' || event.isPrimary === false || this._slides.length < 2) return;
      this._pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, at: Date.now() };
    }

    _onPointerUp(event) {
      const pointer = this._pointer;
      this._pointer = null;
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.25 || Date.now() - pointer.at > 1000) return;
      if (event.cancelable) event.preventDefault();
      this._suppressClickUntil = Date.now() + 400;
      this._show(this._index + (dx < 0 ? 1 : -1), true);
    }
  }

  customElements.define('ba-product-carousel', BaProductCarousel);
})();
